/**
 * `useViewMode` — the Live Agent view's global `Human | Code` response
 * mode (ADR-0163 §3). Mirror of the theme hook's localStorage pattern
 * (`readStoredTheme` / `writeStoredTheme`, `lib/theme.tsx`): default
 * `'human'`, persisted across Run / Reset / agent changes / reloads,
 * storage failures (incognito + cookies blocked) collapse silently to
 * the default. No context provider — localStorage is the source of
 * truth and the view is the only consumer.
 */

import { useCallback, useState } from 'react';

export type ViewMode = 'human' | 'code';

const STORAGE_KEY = 'sill.dashboard.simulation.view';

function readStoredViewMode(): ViewMode | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw: unknown = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'human' || raw === 'code') {
      return raw;
    }
  } catch {
    // Storage may be disabled. Treat as no preference.
  }
  return null;
}

function writeStoredViewMode(mode: ViewMode): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // See readStoredViewMode.
  }
}

export interface UseViewMode {
  mode: ViewMode;
  setMode: (mode: ViewMode) => void;
}

export function useViewMode(): UseViewMode {
  const [mode, setModeRaw] = useState<ViewMode>(() => readStoredViewMode() ?? 'human');
  const setMode = useCallback((next: ViewMode): void => {
    setModeRaw(next);
    writeStoredViewMode(next);
  }, []);
  return { mode, setMode };
}
